import {
  ArrowRightLeft,
  BadgePercent,
  BarChart3,
  Building2,
  Clock,
  CreditCard,
  Crown,
  FileText,
  FolderTree,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Package,
  PlusCircle,
  Printer,
  Receipt,
  Ruler,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Tag,
  TrendingUp,
  Truck,
  UploadCloud,
  UserCog,
  Users,
  Warehouse,
  Wheat,
  type LucideIcon,
} from 'lucide-vue-next'
import { PERMISSIONS, type Permission } from '@/core/auth/permissions'
import { env } from '@/core/config/env'

export interface NavItem {
  label: string
  icon?: LucideIcon
  to?: string
  /** Hanya tampil untuk akun superadmin jika bernilai true. */
  superadminOnly?: boolean
  /** Izin apa pun (any-of) yang membuat item tampil. Tanpa ini = selalu tampil. */
  permission?: Permission | Permission[]
  /** Anak untuk grup collapsible. */
  children?: NavItem[]
}

/** Item navigasi sidebar. Item tanpa `permission` selalu tampil. */
export const navItems: NavItem[] = [
  { label: 'Beranda', icon: LayoutDashboard, to: '/dashboard' },
  {
    label: 'Kasir',
    icon: Receipt,
    to: '/transaksi/kasir',
    permission: PERMISSIONS.transactionCreate,
  },
  {
    label: 'Laporan',
    icon: BarChart3,
    permission: [
      PERMISSIONS.inventoryView,
      PERMISSIONS.cashierReport,
      PERMISSIONS.reportTransactionView,
      PERMISSIONS.reportView,
      PERMISSIONS.reportCentralKitchenView,
    ],
    children: [
      {
        label: 'Riwayat Transaksi',
        icon: Receipt,
        to: '/reports/transactions',
        permission: [PERMISSIONS.reportTransactionView, PERMISSIONS.reportView],
      },
      {
        label: 'Laba Kotor & Margin',
        icon: TrendingUp,
        to: '/reports/gross-profit',
        permission: [PERMISSIONS.inventoryView, PERMISSIONS.reportView],
      },
      {
        label: 'Shift Kasir',
        icon: Clock,
        to: '/reports/shifts',
        permission: PERMISSIONS.cashierReport,
      },
      {
        label: 'Stok & HPP',
        icon: FileText,
        to: '/reports/stock',
        permission: PERMISSIONS.inventoryView,
      },
      {
        label: 'Kartu Stok',
        icon: FileText,
        to: '/stock/card',
        permission: PERMISSIONS.inventoryView,
      },
      {
        label: 'Mutasi Central Kitchen',
        icon: Truck,
        to: '/reports/central-kitchen',
        permission: PERMISSIONS.reportCentralKitchenView,
      },
    ],
  },
  {
    label: 'Produk',
    icon: Package,
    permission: PERMISSIONS.productView,
    children: [
      {
        label: 'Daftar Produk',
        icon: Package,
        to: '/produk/list',
        permission: PERMISSIONS.productView,
      },
      {
        label: 'Kategori',
        icon: FolderTree,
        to: '/produk/kategori',
        permission: PERMISSIONS.productView,
      },
      {
        label: 'Varian',
        icon: Ruler,
        to: '/produk/variant',
        permission: PERMISSIONS.productView,
      },
      {
        label: 'Add-on',
        icon: PlusCircle,
        to: '/produk/addon',
        permission: PERMISSIONS.productView,
      },
      {
        label: 'Grup Add-on',
        icon: Layers,
        to: '/produk/grup-addon',
        permission: PERMISSIONS.productView,
      },
      {
        label: 'Tipe Harga',
        icon: Tag,
        to: '/produk/tipe-harga',
        permission: [PERMISSIONS.priceTypeView, PERMISSIONS.productView],
      },
    ],
  },
  {
    label: 'Metode Pembayaran',
    icon: CreditCard,
    to: '/pembayaran/metode',
    permission: PERMISSIONS.paymentView,
  },
  {
    label: 'Diskon',
    icon: BadgePercent,
    to: '/pembayaran/diskon',
    permission: PERMISSIONS.discountView,
  },
  {
    label: 'Inventaris',
    icon: Warehouse,
    permission: PERMISSIONS.inventoryView,
    children: [
      {
        label: 'Satuan',
        icon: Ruler,
        to: '/inventory/satuan',
        permission: PERMISSIONS.inventoryView,
      },
      {
        label: 'Bahan Baku',
        icon: Wheat,
        to: '/inventory/bahan',
        permission: PERMISSIONS.inventoryView,
      },
      {
        label: 'Resep',
        icon: PlusCircle,
        to: '/inventory/resep',
        permission: PERMISSIONS.inventoryView,
      },
      {
        label: 'Produksi',
        icon: Package,
        to: '/inventory/productions',
        permission: PERMISSIONS.inventoryView,
      },
    ],
  },
  {
    label: 'Manajemen Stok',
    icon: Receipt,
    permission: PERMISSIONS.inventoryView,
    children: [
      {
        label: 'Pesanan Pembelian (PO)',
        icon: Receipt,
        to: '/inventory/purchases',
        permission: PERMISSIONS.inventoryView,
      },
      {
        label: 'Transfer Antar Outlet',
        icon: ArrowRightLeft,
        to: '/inventory/transfers',
        permission: PERMISSIONS.stockTransfer,
      },
      {
        label: 'Penyesuaian Stok',
        icon: SlidersHorizontal,
        to: '/stock/adjustments',
        permission: PERMISSIONS.stockAdjust,
      },
    ],
  },
  {
    label: 'Pengguna & Akses',
    icon: ShieldCheck,
    permission: [PERMISSIONS.userView, PERMISSIONS.roleView],
    children: [
      {
        label: 'Pengguna',
        icon: UserCog,
        to: '/users-access/accounts',
        permission: PERMISSIONS.userView,
      },
      {
        label: 'Akses',
        icon: ShieldCheck,
        to: '/users-access/roles',
        permission: PERMISSIONS.roleView,
      },
    ],
  },
  ...(env.featureTableLayout
    ? [
        {
          label: 'Pengaturan Layout Meja',
          icon: LayoutGrid,
          to: '/pengaturan/layout-meja',
          // Saat mock, `table.view` belum ada di katalog permission BE — tidak
          // ada token yang memuatnya, jadi menu akan hilang untuk semua orang.
          permission: env.useMockTables ? undefined : PERMISSIONS.tableView,
        },
      ]
    : []),
  { label: 'Pengaturan Printer', icon: Printer, to: '/pengaturan/printer' },
  { label: 'Pengaturan Outlet', icon: Settings, to: '/pengaturan/outlet' },
  {
    label: 'MANAGEMENT SUPERADMIN',
    icon: Crown,
    superadminOnly: true,
    children: [
      {
        label: 'Kelola Tenant',
        icon: Store,
        to: '/superadmin/tenants',
        superadminOnly: true,
      },
      {
        label: 'Subscriber Outlet',
        icon: Building2,
        to: '/superadmin/subscribers',
        superadminOnly: true,
      },
      {
        label: 'Restore Backup Offline',
        icon: UploadCloud,
        to: '/superadmin/restore-backup',
        superadminOnly: true,
      },
    ],
  },
]
